<template>
  <div class="support-page">
    <!-- Header -->
    <section class="page-header">
      <div class="container">
        <h1>{{ t('support.title') }}</h1>
        <p class="page-subtitle">{{ t('support.subtitle') }}</p>
      </div>
    </section>

    <!-- Support Content -->
    <section class="support-content">
      <div class="container">
        <!-- Quick Help -->
        <div class="quick-help">
          <h2>{{ t('support.quickHelp') }}</h2>
          <div class="help-categories">
            <div class="help-card">
              <div class="help-icon">❓</div>
              <h3>{{ t('support.faq') }}</h3>
              <p>{{ t('support.faqDesc') }}</p>
              <a href="#/faq" class="card-link">{{ t('buttons.view') }}</a>
            </div>
            <div class="help-card">
              <div class="help-icon">📖</div>
              <h3>{{ t('support.howToPlay') }}</h3>
              <p>{{ t('support.howToPlayDesc') }}</p>
              <a href="#/how-to-play" class="card-link">{{ t('buttons.view') }}</a>
            </div>
            <div class="help-card">
              <div class="help-icon">💡</div>
              <h3>{{ t('support.tips') }}</h3>
              <p>{{ t('support.tipsDesc') }}</p>
              <a href="#/tips" class="card-link">{{ t('buttons.view') }}</a>
            </div>
          </div>
        </div>

        <!-- Common Issues -->
        <div class="common-issues">
          <h2>{{ t('support.commonIssues') }}</h2>
          <div class="issues-list">
            <div class="issue-item">
              <h3>{{ t('support.issue1.title') }}</h3>
              <p>{{ t('support.issue1.solution') }}</p>
            </div>
            <div class="issue-item">
              <h3>{{ t('support.issue2.title') }}</h3>
              <p>{{ t('support.issue2.solution') }}</p>
            </div>
            <div class="issue-item">
              <h3>{{ t('support.issue3.title') }}</h3>
              <p>{{ t('support.issue3.solution') }}</p>
            </div>
            <div class="issue-item">
              <h3>{{ t('support.issue4.title') }}</h3>
              <p>{{ t('support.issue4.solution') }}</p>
            </div>
          </div>
        </div>

        <!-- Contact Support -->
        <div class="contact-support">
          <h2>{{ t('support.stillNeedHelp') }}</h2>
          <p>{{ t('support.contactUsDesc') }}</p>
          <button class="btn-contact" @click="goToContact">{{ t('nav.contact') }}</button>
        </div>

        <!-- Back Button -->
        <button class="btn-back" @click="goHome">{{ t('buttons.backHome') }}</button>
      </div>
    </section>
  </div>
</template>

<script setup>
import { useI18n } from '@/utils/i18n'

const { t } = useI18n()

const goToContact = () => {
  window.location.hash = '#/contact'
}

const goHome = () => {
  window.location.hash = '#/'
}
</script>

<style scoped>
.support-page {
  min-height: 100vh;
  background: var(--color-bg-light);
}

/* Header */
.page-header {
  background: var(--gradient-primary);
  color: white;
  padding: var(--space-5xl) var(--space-base);
  text-align: center;
}

.page-header h1 {
  font-size: clamp(28px, 6vw, 48px);
  margin: 0 0 var(--space-base) 0;
  font-weight: var(--font-weight-bold);
}

.page-subtitle {
  font-size: var(--font-size-body);
  margin: 0;
  opacity: 0.95;
}

/* Content */
.container {
  max-width: 1000px;
  margin: 0 auto;
  padding: var(--space-4xl) var(--space-base);
}

.support-content {
  background: var(--color-bg-light);
}

/* Sections */
.quick-help,
.common-issues,
.contact-support {
  margin-bottom: var(--space-5xl);
}

h2 {
  font-size: var(--font-size-h3);
  color: var(--color-text-primary);
  margin: 0 0 var(--space-2xl) 0;
  font-weight: var(--font-weight-bold);
  text-align: center;
}

/* Quick Help */
.help-categories {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--space-2xl);
}

.help-card {
  background: var(--color-bg-card);
  padding: var(--space-2xl);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border);
  text-align: center;
  transition: all var(--transition-base) var(--ease-in-out));
}

.help-card:hover {
  box-shadow: var(--shadow-lg);
  transform: translateY(-4px);
}

.help-icon {
  font-size: 48px;
  margin-bottom: var(--space-lg);
  display: block;
}

.help-card h3 {
  font-size: var(--font-size-h5);
  color: var(--color-text-primary);
  margin: 0 0 var(--space-md) 0;
  font-weight: var(--font-weight-bold);
}

.help-card p {
  font-size: var(--font-size-small);
  color: var(--color-text-secondary);
  margin: 0 0 var(--space-lg) 0;
  line-height: 1.6;
}

.card-link {
  display: inline-block;
  color: var(--color-primary-orange);
  text-decoration: none;
  font-weight: var(--font-weight-semibold);
  transition: all var(--transition-base) var(--ease-in-out));
  padding: var(--space-sm) var(--space-md);
  border-radius: var(--radius-md);
}

.card-link:hover {
  background: rgba(255, 107, 53, 0.1);
  color: var(--color-primary-gold);
}

/* Common Issues */
.issues-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: var(--space-2xl);
}

.issue-item {
  background: var(--color-bg-light);
  padding: var(--space-2xl);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border);
  transition: all var(--transition-base) var(--ease-in-out));
}

.issue-item:hover {
  border-color: var(--color-primary-orange);
  box-shadow: var(--shadow-md);
}

.issue-item h3 {
  font-size: var(--font-size-h5);
  color: var(--color-text-primary);
  margin: 0 0 var(--space-md) 0;
  font-weight: var(--font-weight-bold);
}

.issue-item p {
  font-size: var(--font-size-small);
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.7;
}

/* Contact Support */
.contact-support {
  background: var(--gradient-primary);
  color: white;
  padding: var(--space-3xl);
  border-radius: var(--radius-lg);
  text-align: center;
}

.contact-support h2 {
  color: white;
}

.contact-support p {
  font-size: var(--font-size-body);
  margin: 0 0 var(--space-2xl) 0;
  opacity: 0.95;
}

.btn-contact {
  display: inline-block;
  padding: var(--space-md) var(--space-2xl);
  background: var(--color-bg-light);
  color: var(--color-primary-orange);
  border: none;
  border-radius: var(--radius-md);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all var(--transition-base) var(--ease-in-out));
  font-family: var(--font-heading);
  font-size: var(--font-size-body);
}

.btn-contact:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

/* Back Button */
.btn-back {
  display: block;
  margin: var(--space-3xl) auto 0;
  padding: var(--space-md) var(--space-2xl);
  background: var(--color-bg-light);
  color: var(--color-text-primary);
  border: 2px solid var(--color-border);
  border-radius: var(--radius-md);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all var(--transition-base) var(--ease-in-out));
  font-family: var(--font-heading);
  font-size: var(--font-size-body);
}

.btn-back:hover {
  border-color: var(--color-primary-orange);
  color: var(--color-primary-orange);
  background: rgba(255, 107, 53, 0.05);
}

/* Responsive */
@media (max-width: 640px) {
  .page-header {
    padding: var(--space-3xl) var(--space-base);
  }

  .container {
    padding: var(--space-2xl) var(--space-base);
  }

  .quick-help,
  .common-issues,
  .contact-support {
    margin-bottom: var(--space-3xl);
  }

  h2 {
    font-size: var(--font-size-h4);
    margin-bottom: var(--space-lg);
  }

  .help-categories,
  .issues-list {
    grid-template-columns: 1fr;
    gap: var(--space-lg);
  }

  .help-card,
  .issue-item {
    padding: var(--space-lg);
  }

  .contact-support {
    padding: var(--space-2xl);
  }

  .btn-back {
    width: 100%;
  }
}
</style>
